import {
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';

import { type HourOfDayPoint } from '@apps/web/components/admin/outreach_observability/comparison_utils';

const TODAY_KEY = 'today';
const HOUR_TICKS = [0, 4, 8, 12, 16, 20];

type GhostSeries = {
    readonly label: string;
    readonly points: Array<HourOfDayPoint>;
};

type GhostLineChartProps = {
    readonly title?: string;
    // Today's partial series on an ET hour-of-day (0–23) axis.
    readonly today: Array<HourOfDayPoint>;
    // Muted reference series (yesterday, 7 days ago) time-aligned to the same
    // hour-of-day axis. They are context, never competing series.
    readonly ghosts: Array<GhostSeries>;
    readonly valueFormatter: (value: number) => string;
    // When true the caller has already accumulated the points (running totals);
    // this component only renders — it never accumulates.
    readonly cumulative?: boolean;
    readonly height?: number;
    // Fires when a point on any series is clicked. `seriesKey` is 'today' for
    // the primary series or 'ghost_<index>' for a reference series, so the
    // caller can map it back to the ET date it built the series from.
    readonly onPointClick?: (args: {
        readonly hourOfDay: number;
        readonly seriesKey: string;
    }) => void;
};

type SeriesMeta = {
    readonly key: string;
    readonly label: string;
    readonly color: string;
    readonly ghost: boolean;
};

type GhostChartRow = {
    readonly hourOfDay: number;
    readonly [seriesKey: string]: number | null;
};

function formatHourOfDay(hour: number): string {
    const period = hour < 12 ? 'am' : 'pm';
    const display = hour % 12 === 0 ? 12 : hour % 12;
    return `${display}${period}`;
}

function valueAtHour(
    points: Array<HourOfDayPoint>,
    hour: number,
): number | null {
    return points.find((point) => point.hourOfDay === hour)?.value ?? null;
}

function GhostTooltip({
    active,
    payload,
    label,
    seriesMeta,
    valueFormatter,
}: {
    readonly active?: boolean;
    readonly payload?: Array<{
        readonly dataKey?: string | number;
        readonly value?: unknown;
    }>;
    readonly label?: number;
    readonly seriesMeta: Array<SeriesMeta>;
    readonly valueFormatter: (value: number) => string;
}) {
    if (!active || payload === undefined || payload.length === 0) {
        return null;
    }
    return (
        <div className="bg-background rounded-md border px-3 py-2 text-xs shadow-sm">
            <div className="mb-1 font-medium">
                {label === undefined ? '' : formatHourOfDay(label)}
            </div>
            {seriesMeta.map((series) => {
                const entry = payload.find((row) => row.dataKey === series.key);
                const value = entry === undefined ? null : rowValue(entry);
                return (
                    <div
                        key={series.key}
                        className="flex items-center justify-between gap-4"
                    >
                        <span className="flex items-center gap-1.5">
                            <span
                                className="inline-block h-0 w-3 border-t-2"
                                style={{
                                    borderColor: series.color,
                                    borderTopStyle: series.ghost
                                        ? 'dashed'
                                        : 'solid',
                                }}
                            />
                            <span className="text-muted-foreground">
                                {series.label}
                            </span>
                        </span>
                        <span className="tabular-nums">
                            {value === null ? '—' : valueFormatter(value)}
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

function rowValue(entry: { readonly value?: unknown }): number | null {
    return typeof entry.value === 'number' ? entry.value : null;
}

export function GhostLineChart({
    title,
    today,
    ghosts,
    valueFormatter,
    height = 240,
    onPointClick,
}: GhostLineChartProps) {
    const seriesMeta: Array<SeriesMeta> = [
        {
            key: TODAY_KEY,
            label: 'Today',
            color: 'var(--chart-1)',
            ghost: false,
        },
        ...ghosts.map((ghost, index) => ({
            key: `ghost_${index}`,
            label: ghost.label,
            color: 'var(--muted-foreground)',
            ghost: true,
        })),
    ];
    const rows: Array<GhostChartRow> = Array.from(
        { length: 24 },
        (_unused, hour) => ({
            hourOfDay: hour,
            [TODAY_KEY]: valueAtHour(today, hour),
            ...Object.fromEntries(
                ghosts.map((ghost, index) => [
                    `ghost_${index}`,
                    valueAtHour(ghost.points, hour),
                ]),
            ),
        }),
    );
    return (
        <div className="flex flex-col gap-2">
            {title ? <div className="text-sm font-medium">{title}</div> : null}
            <div className="flex flex-wrap items-center gap-3 text-xs">
                {seriesMeta.map((series) => (
                    <span
                        key={series.key}
                        className="flex items-center gap-1.5"
                    >
                        <span
                            className="inline-block h-0 w-4 border-t-2"
                            style={{
                                borderColor: series.color,
                                borderTopStyle: series.ghost
                                    ? 'dashed'
                                    : 'solid',
                            }}
                        />
                        <span className="text-muted-foreground">
                            {series.label}
                        </span>
                    </span>
                ))}
            </div>
            <div className="w-full" style={{ height }}>
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={rows} margin={{ left: 8, right: 8 }}>
                        <CartesianGrid
                            stroke="var(--border)"
                            vertical={false}
                        />
                        <XAxis
                            dataKey="hourOfDay"
                            type="number"
                            domain={[0, 23]}
                            ticks={HOUR_TICKS}
                            tick={{ fontSize: 11 }}
                            tickFormatter={formatHourOfDay}
                        />
                        <YAxis
                            tick={{ fontSize: 11 }}
                            width={48}
                            tickFormatter={valueFormatter}
                        />
                        <RechartsTooltip
                            content={
                                <GhostTooltip
                                    seriesMeta={seriesMeta}
                                    valueFormatter={valueFormatter}
                                />
                            }
                        />
                        {seriesMeta.map((series) => (
                            <Line
                                key={series.key}
                                type="monotone"
                                dataKey={series.key}
                                name={series.label}
                                stroke={series.color}
                                strokeWidth={series.ghost ? 1.5 : 2}
                                strokeDasharray={
                                    series.ghost ? '4 4' : undefined
                                }
                                strokeOpacity={series.ghost ? 0.6 : 1}
                                dot={false}
                                activeDot={
                                    onPointClick
                                        ? (props: {
                                              readonly cx?: number;
                                              readonly cy?: number;
                                              readonly payload?: GhostChartRow;
                                          }) => {
                                              if (
                                                  props.cx === undefined ||
                                                  props.cy === undefined ||
                                                  props.payload === undefined
                                              ) {
                                                  return <circle r={0} />;
                                              }
                                              const hourOfDay =
                                                  props.payload.hourOfDay;
                                              return (
                                                  <circle
                                                      cx={props.cx}
                                                      cy={props.cy}
                                                      r={5}
                                                      fill={series.color}
                                                      style={{
                                                          cursor: 'pointer',
                                                      }}
                                                      onClick={() =>
                                                          onPointClick({
                                                              hourOfDay,
                                                              seriesKey:
                                                                  series.key,
                                                          })
                                                      }
                                                  />
                                              );
                                          }
                                        : undefined
                                }
                                connectNulls={false}
                                isAnimationActive={false}
                            />
                        ))}
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
